/**
 * Health Indicators Component
 *
 * Displays 2 Traction-style health indicators (traffic lights):
 * - Collections Health (from Outstanding Invoices)
 * - Pipeline Health (from Revenue Pipeline)
 *
 * Note: Cash Health and Project Health deferred to Phase 10
 * (require bank balance integration and expense tracking respectively)
 */
import {
  useOutstandingInvoices,
  useRevenuePipeline,
} from '@/hooks/useDashboardReports';
import {
  calculateCollectionsHealth,
  calculateActivePipelineHealth,
  getHealthStatusClasses,
  getHealthStatusIcon,
  type HealthIndicator,
  type HealthIconName,
} from '@/lib/dashboardHealth';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, XCircle, Info, type LucideIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

// Map icon names to Lucide components
const HEALTH_ICONS: Record<HealthIconName, LucideIcon> = {
  'check-circle': CheckCircle2,
  'alert-circle': AlertCircle,
  'x-circle': XCircle,
};

// Detailed calculation tooltips for each health indicator
const HEALTH_TOOLTIPS: Record<string, string> = {
  'Collections': 'Percentage of outstanding invoices that are overdue. Green: <10% overdue. Yellow: 10-30% overdue. Red: >30% overdue.',
  'Pipeline': 'Value of active proposals (<30 days old). Green: 3+ proposals & $50k+. Yellow: Some proposals. Red: No active proposals.',
};

// Format currency short
const formatCurrencyShort = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${Math.round(value / 1000)}k`;
  }
  return `$${value}`;
};

export function HealthIndicators() {
  const { data: invoicesData, isLoading: invoicesLoading } = useOutstandingInvoices();
  const { data: pipelineData, isLoading: pipelineLoading } = useRevenuePipeline(6);

  // Calculate health indicators (only 2 for now)
  const indicators: (HealthIndicator | null)[] = [];

  // Collections Health
  if (!invoicesLoading && invoicesData) {
    const invoices = invoicesData as any;
    indicators.push(
      calculateCollectionsHealth(
        invoices.summary?.total_outstanding || 0,
        invoices.summary?.total_overdue || 0
      )
    );
  } else {
    indicators.push(null);
  }

  // Pipeline Health (Active proposals only for dashboard)
  if (!pipelineLoading && pipelineData) {
    const pipeline = pipelineData as any;
    indicators.push(
      calculateActivePipelineHealth(
        pipeline.proposals?.active?.total_value || 0,
        pipeline.proposals?.active?.count || 0,
        pipeline.proposals?.active?.regular_value || 0,
        pipeline.proposals?.active?.optional_value || 0
      )
    );
  } else {
    indicators.push(null);
  }

  const isLoading = invoicesLoading || pipelineLoading;

  if (isLoading) {
    return (
      <>
        {[1, 2].map((i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-10 bg-gray-100 rounded mb-1"></div>
            <div className="h-3 bg-gray-100 rounded w-24"></div>
          </div>
        ))}
      </>
    );
  }

  return (
    <TooltipProvider>
      {indicators.map((indicator, index) => {
        if (!indicator) {
          return <div key={index} className="bg-white p-6 rounded-lg shadow text-center text-gray-400 text-sm">Loading...</div>;
        }

        const classes = getHealthStatusClasses(indicator.status);
        const iconName = getHealthStatusIcon(indicator.status);
        const IconComponent = HEALTH_ICONS[iconName];
        const tooltipText = HEALTH_TOOLTIPS[indicator.label] || indicator.description;

        // Check if this indicator has regular/optional values (Pipeline indicator)
        const hasRegularOptional = 'regularValue' in indicator && 'optionalValue' in indicator;
        const regularValue = hasRegularOptional ? (indicator as any).regularValue : 0;
        const optionalValue = hasRegularOptional ? (indicator as any).optionalValue : 0;

        const cardContent = (
          <div
            className={`${classes.bg} ${classes.border} border rounded-lg p-6 shadow hover:shadow-md transition-all h-full ${indicator.link ? 'cursor-pointer' : ''}`}
            data-testid={`health-${indicator.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-gray-700">{indicator.label}</span>
                <Info size={14} className="text-gray-400" aria-hidden="true" />
              </div>
              <div className={`${classes.bg} p-2 rounded-lg`}>
                <IconComponent className={`w-5 h-5 ${classes.icon}`} />
              </div>
            </div>
            <div className={`text-2xl font-bold ${classes.text}`}>
              {indicator.value}
            </div>
            <p className="text-xs text-gray-600 mt-1">{indicator.description}</p>
            {hasRegularOptional && (
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrencyShort(regularValue)} regular · {formatCurrencyShort(optionalValue)} optional
              </p>
            )}
          </div>
        );

        // Wrap in tooltip and optionally link
        const tooltipWrapped = (
          <Tooltip key={indicator.label}>
            <TooltipTrigger asChild>
              {indicator.link ? (
                <Link to={indicator.link} className="block">
                  {cardContent}
                </Link>
              ) : (
                cardContent
              )}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        );

        return tooltipWrapped;
      })}
    </TooltipProvider>
  );
}
